import { calendarConnectionTimes } from "../lib/calendarConnectionTimes";
import {
  addDays,
  addMonths,
  format,
  fromUnixTime,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { doc, updateDoc } from "firebase/firestore";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";
import posthog from "posthog-js";
import {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Calendar from "react-calendar";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { usePostAnalytics } from "../hooks/usePostAnalytics";
import { useScheduledPosts } from "../hooks/useScheduledPosts";
import { cn, formatDate, loadMediaUrls } from "../lib/utils";
import { PlatformConnection } from "../models/Platform";
import { Post } from "../models/Post";
import { db } from "../services/Firebase";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { analyticsReconnectNeeded, PostMetricsRow } from "./PostMetricsRow";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import { PostSource } from "./PostSource";
import { PostViewer } from "./PostViewer";
import { SlidePreview } from "./SlidePreview";
import { Button } from "./ui/button";
import "./ui/calendar.css";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { toast } from "./ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const MAX_VISIBLE_CONNECTIONS = 6;
const MAX_VISIBLE_CONNECTIONS_MONTH = 2;
const MAX_VISIBLE_MONTH_ENTRIES = 2;

// Color palette for post grouping (visually distinct, accessible colors)
const POST_COLORS = [
  "#3B82F6", // blue
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#F59E0B", // amber
  "#10B981", // emerald
  "#06B6D4", // cyan
  "#EF4444", // red
  "#84CC16", // lime
];

// Generate a consistent color for a post based on its ID
const getPostColor = (postId: string): string => {
  let hash = 0;
  for (let i = 0; i < postId.length; i++) {
    hash = postId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return POST_COLORS[Math.abs(hash) % POST_COLORS.length];
};

// Represents a single calendar entry (one connection of a post)
interface CalendarEntry {
  post: Post;
  connectionId: string;
  scheduledFor?: number;
  publishedAt?: number;
  status: string;
  // Grouping info
  groupIndex: number; // 1-based index within the post's entries
  groupTotal: number; // total entries for this post
  postColor: string; // color for visual grouping
}

const getDateKey = (timestamp?: number): string => {
  if (!timestamp) return "";

  // Convert milliseconds to seconds if needed
  const seconds =
    timestamp.toString().length > 10 ? timestamp / 1000 : timestamp;
  return format(fromUnixTime(seconds), "yyyy/MM/dd");
};

const toMilliseconds = (timestamp?: number): number => {
  if (!timestamp) return 0;
  return timestamp.toString().length > 10 ? timestamp : timestamp * 1000;
};

const getConnectionStatusLabel = (
  statuses: string[],
): "Published" | "Failed" | "Processing" | "Scheduled" | "Mixed" => {
  if (statuses.length === 0) return "Scheduled";
  if (statuses.every((status) => status === "published")) return "Published";
  if (statuses.every((status) => status === "failed")) return "Failed";
  if (
    statuses.some(
      (status) =>
        status === "pre-processing" ||
        status === "publishing" ||
        status === "generating",
    )
  ) {
    return "Processing";
  }
  if (
    statuses.every((status) => status === "pending" || status === "scheduled")
  )
    return "Scheduled";
  return "Mixed";
};

const getStatusBadgeClass = (statusLabel: string): string => {
  switch (statusLabel) {
    case "Published":
      return "bg-green-100 text-green-700";
    case "Failed":
      return "bg-red-100 text-red-700";
    case "Processing":
    case "Generating":
      return "bg-indigo-100 text-indigo-700";
    case "Mixed":
      return "bg-yellow-100 text-yellow-700";
    case "Draft":
      return "bg-gray-100 text-gray-700";
    case "Scheduled":
    default:
      return "bg-blue-100 text-blue-700";
  }
};

const QUICK_PEEK_OPEN_DELAY_MS = 300;
const QUICK_PEEK_WARM_WINDOW_MS = 500;
// Shared across all entries: once a peek was open (or just closed), the next
// entry's peek opens instantly for a short window, so sweeping across the
// calendar feels continuous instead of re-paying the open delay per entry
// (same idea as Radix Tooltip's skipDelayDuration, which HoverCard lacks).
let lastPeekActiveAt = 0;

// The peek is a pointer affordance. On touch-primary devices tap goes
// straight to the PostViewer, and rendering the trigger would arm Radix's
// focus-open path (e.g. tapping an entry's X button focuses the trigger and
// pops an undismissable card).
const HOVER_CAPABLE =
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(hover: hover)").matches;

// Per-connection rows inside the quick-peek, with engagement metrics under
// published rows. Lives in its own component (mounted only while the card is
// open) so the post_analytics subscription is one-per-opened-peek, not
// one-per-calendar-entry.
const QuickPeekConnectionRows = memo(function QuickPeekConnectionRows({
  post,
  connectionRows,
  connectionsById,
}: {
  post: Post;
  connectionRows: string[];
  connectionsById: Record<string, PlatformConnection>;
}) {
  const analyticsUiEnabled = useAnalyticsUiEnabled();
  const hasPublished = connectionRows.some(
    (connId) => post.connections[connId]?.status === "published",
  );
  const { byConnectionId } = usePostAnalytics(
    analyticsUiEnabled && hasPublished ? post.id : null,
  );

  return (
    <div className="border-t border-gray-100 px-3 py-2 space-y-1.5 max-h-40 overflow-y-auto">
      {connectionRows.map((connId) => {
        const conn = post.connections[connId];
        const details = connectionsById[connId];
        return (
          <div key={connId} className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <PlatformConnectionIcon
                platform={details.platform}
                profileImage={details.profileImage}
                size="xs"
                disableTooltip={true}
              />
              <span className="text-xs text-gray-700 truncate min-w-0 flex-1">
                {details.profileName}
              </span>
              <span className="text-[11px] text-gray-500 shrink-0">
                {conn.publishedAt
                  ? `Published ${formatDate(conn.publishedAt)}`
                  : conn.status === "failed"
                    ? "Failed"
                    : conn.status === "pre-processing" ? "Preparing"
                    : conn.status === "publishing" ? "Publishing"
                    : !conn.scheduledFor && !post.scheduledFor ? "Queued"
                    : `Scheduled ${formatDate(conn.scheduledFor ?? post.scheduledFor)}`}
              </span>
            </div>
            {analyticsUiEnabled && conn.status === "published" && (
              <div className="pl-6">
                <PostMetricsRow
                  analytics={byConnectionId[connId]}
                  reconnectHint={analyticsReconnectNeeded(details)}
                  connectionUpdatedAt={details.updatedAt}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

// Quick-peek hover card: read a post (rendered slide + full caption +
// per-platform status) by hovering, without opening the full PostViewer
// modal. Clicking the entry opens the PostViewer directly, so the card
// closes itself on click and on drag.
const EntryQuickPeek = memo(function EntryQuickPeek({
  post,
  connectionIds,
  connectionsById,
  statusLabel,
  mediaUrls,
  onOpenPost,
  disabled,
  children,
}: {
  post: Post;
  connectionIds: string[];
  connectionsById: Record<string, PlatformConnection>;
  statusLabel: string;
  mediaUrls: Record<string, string>;
  onOpenPost: () => void;
  disabled?: boolean;
  children: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  // Clicking the entry opens the PostViewer; Radix's pending open-delay timer
  // would still fire and pop the card over the modal. Suppress opens after a
  // click/drag until the pointer re-enters the trigger.
  const suppressOpenRef = useRef(false);

  if (disabled || !HOVER_CAPABLE) return children;

  const closeAndSuppress = () => {
    suppressOpenRef.current = true;
    setOpen(false);
  };

  const caption = post.description?.trim() || post.title?.trim() || "";
  const firstMediaItem = post.mediaItems?.[0];
  const slideCount = post.mediaItems?.length ?? 0;
  const connectionRows = connectionIds.filter(
    (connId) => post.connections[connId] && connectionsById[connId],
  );

  return (
    <HoverCard
      open={open}
      onOpenChange={(next) => {
        if (next && suppressOpenRef.current) return;
        if (!next) lastPeekActiveAt = Date.now();
        setOpen(next);
      }}
      openDelay={QUICK_PEEK_OPEN_DELAY_MS}
      closeDelay={100}
    >
      <HoverCardTrigger
        asChild
        onClick={closeAndSuppress}
        onDragStart={closeAndSuppress}
        onPointerEnter={() => {
          suppressOpenRef.current = false;
          if (Date.now() - lastPeekActiveAt < QUICK_PEEK_WARM_WINDOW_MS) {
            setOpen(true);
          }
        }}
        onPointerLeave={() => {
          // Stamp while still open so a fast move to the next entry lands
          // inside the warm window even before the close delay fires.
          if (open) lastPeekActiveAt = Date.now();
        }}
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className="w-80 p-0 overflow-hidden"
      >
        <div className="flex gap-3 p-3">
          {firstMediaItem && (
            <div className="shrink-0 self-start max-w-[10rem] rounded-md overflow-hidden border border-gray-100 bg-gray-50 flex justify-center">
              <SlidePreview
                mediaItem={firstMediaItem}
                mediaUrls={mediaUrls}
                className="h-36 max-w-full"
                eagerOverlays
              />
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={cn(
                  "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
                  getStatusBadgeClass(statusLabel),
                )}
              >
                {statusLabel}
              </span>
              {slideCount > 1 && (
                <span className="text-[10px] text-gray-400">
                  {slideCount} slides
                </span>
              )}
            </div>
            {caption ? (
              <p
                className="notranslate text-xs text-gray-800 whitespace-pre-wrap max-h-32 overflow-y-auto pr-1"
                translate="no"
              >
                {caption}
              </p>
            ) : (
              <p className="text-xs text-gray-400 italic">No caption</p>
            )}
          </div>
        </div>
        {post.generationSource && (
          <div className="border-t border-gray-100 px-3 py-2 flex items-baseline gap-2 min-w-0">
            <span className="text-[11px] font-medium text-gray-500 shrink-0">
              Source
            </span>
            <PostSource
              source={post.generationSource}
              className="text-xs truncate min-w-0"
            />
          </div>
        )}
        {connectionRows.length > 0 && (
          <QuickPeekConnectionRows
            post={post}
            connectionRows={connectionRows}
            connectionsById={connectionsById}
          />
        )}
        <div className="border-t border-gray-100 p-2">
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => {
              setOpen(false);
              onOpenPost();
            }}
          >
            Open post
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
});

// Memoize TileContent component for better performance
const TileContent = memo(function TileContent({
  date,
  view,
  entriesByDate,
  connectionsById,
  onPostSelect,
  onDrop,
  onCancelSchedule,
}: {
  date: Date;
  view: string;
  entriesByDate: Record<string, CalendarEntry[]>;
  connectionsById: Record<string, PlatformConnection>;
  onPostSelect: (post: Post) => void;
  onDrop: (postId: string, connectionIds: string[], date: Date) => void;
  onCancelSchedule: (postId: string, connectionIds: string[]) => void;
}) {
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMonthExpanded, setIsMonthExpanded] = useState(false);
  const dateStr = getDateKey(date.getTime());

  // Get entries for this day, sorted by time
  const dayEntries = useMemo(() => {
    const entries = entriesByDate[dateStr] ?? [];
    return entries.sort((a, b) => {
      const timeA = a.scheduledFor ?? a.publishedAt ?? 0;
      const timeB = b.scheduledFor ?? b.publishedAt ?? 0;
      return timeA - timeB;
    });
  }, [entriesByDate, dateStr]);

  // Get unique posts for media loading
  const dayPosts = useMemo(() => {
    const uniquePosts = new Map<string, Post>();
    dayEntries.forEach((entry) => {
      if (!uniquePosts.has(entry.post.id)) {
        uniquePosts.set(entry.post.id, entry.post);
      }
    });
    return Array.from(uniquePosts.values());
  }, [dayEntries]);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const isMonthView = view === "month";
  const maxVisibleConnections = isMonthView
    ? MAX_VISIBLE_CONNECTIONS_MONTH
    : MAX_VISIBLE_CONNECTIONS;

  useEffect(() => {
    setIsMonthExpanded(false);
  }, [dateStr, isMonthView]);

  const entriesToRender = useMemo(() => {
    if (!isMonthView || isMonthExpanded) return dayEntries;
    return dayEntries.slice(0, MAX_VISIBLE_MONTH_ENTRIES);
  }, [dayEntries, isMonthView, isMonthExpanded]);

  const hasMonthToggle =
    isMonthView && dayEntries.length > MAX_VISIBLE_MONTH_ENTRIES;
  const useMonthTwoRowLayout =
    isMonthView &&
    !isMonthExpanded &&
    entriesToRender.length === MAX_VISIBLE_MONTH_ENTRIES;

  // Load media URLs only when posts change
  useEffect(() => {
    let isMounted = true;
    const loadUrls = async () => {
      if (dayPosts.length === 0) return;
      try {
        const urlEntries = await loadMediaUrls(dayPosts);
        if (isMounted) {
          setMediaUrls(urlEntries);
        }
      } catch (error) {
        console.error("Error loading media URLs:", error);
      }
    };

    loadUrls();
    return () => {
      isMounted = false;
    };
  }, [dayPosts]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = dropZoneRef.current?.getBoundingClientRect();
    if (rect) {
      const { left, right, top, bottom } = rect;
      if (
        e.clientX < left ||
        e.clientX >= right ||
        e.clientY < top ||
        e.clientY >= bottom
      ) {
        setIsDragOver(false);
      }
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      try {
        const dragDataStr = e.dataTransfer.getData("text/plain");
        if (!dragDataStr) return;

        // Parse drag data (JSON with postId and connectionIds)
        let postId: string;
        let connectionIds: string[] = [];
        try {
          const dragData = JSON.parse(dragDataStr);
          postId = dragData.postId;
          connectionIds = dragData.connectionIds ?? [];
        } catch {
          // Fallback for old format (just postId string)
          postId = dragDataStr;
        }

        // Reset cursor
        document.body.style.cursor = "default";
        onDrop(postId, connectionIds, date);
      } catch (error) {
        console.error("Error handling drop:", error);
        toast({
          title: "Error",
          description: "Failed to reschedule post. Please try again.",
          variant: "destructive",
        });
      }
    },
    [date, onDrop],
  );

  return (
    <div
      ref={dropZoneRef}
      className={cn(
        "rounded-lg transition-all duration-200",
        isMonthView ? "min-h-[40px]" : "min-h-16 md:min-h-60",
        isDragOver
          ? "bg-[#FF5C35]/10 outline-dashed outline-2 outline-[#FF5C35] scale-105"
          : "hover:bg-gray-50/50",
        dayEntries.length > 0 && (isMonthView ? "mt-1" : "space-y-2"),
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnd={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {dayEntries.length > 0 ? (
        <div
          className={cn(
            isMonthView
              ? "h-full flex flex-col overflow-hidden"
              : "overflow-visible",
          )}
        >
          <div
            className={cn(
              isMonthView ? "flex-1 min-h-0" : "overflow-visible",
              useMonthTwoRowLayout
                ? "grid grid-rows-2 gap-1.5"
                : isMonthView && isMonthExpanded
                  ? "space-y-1.5 overflow-y-auto scroll-smooth"
                  : isMonthView
                    ? "space-y-1.5 overflow-hidden"
                    : "space-y-2",
            )}
          >
            {entriesToRender.map((entry) => {
              const {
                post,
                connectionId,
                groupIndex,
                groupTotal,
                postColor,
                scheduledFor,
                publishedAt,
              } = entry;
              const isGrouped = groupTotal > 1;
              const showAllConnections = connectionId === "__all__";
              const isTimeGrouped = connectionId.startsWith("__time_");

              // Get the connections to display for this entry
              let connectionsForEntry: string[];
              if (showAllConnections) {
                connectionsForEntry = Object.keys(post.connections);
              } else if (isTimeGrouped) {
                // Extract timestamp from marker and find connections at that time
                const entryTime = scheduledFor || publishedAt;
                connectionsForEntry = Object.entries(post.connections)
                  .filter(([id]) => {
                    const connTime = calendarConnectionTimes(post)[id];
                    return connTime === entryTime;
                  })
                  .map(([id]) => id);
              } else {
                connectionsForEntry = [connectionId];
              }

              // For grouped entries, check if all connections in this entry are pending
              const isPending = connectionsForEntry.every(
                (connId) => post.connections[connId]?.status === "pending",
              );
              const isBatchPost = !!post.batchId;
              const isBatchDraft = isBatchPost && post.status === "draft";
              const isBatchGenerating =
                isBatchPost && post.status === "generating";

              const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
                if (!isPending || !e.currentTarget) {
                  e.preventDefault();
                  return;
                }
                try {
                  // Store postId and the specific connections in this entry
                  const dragData = JSON.stringify({
                    postId: post.id,
                    connectionIds: connectionsForEntry,
                  });
                  e.dataTransfer.setData("text/plain", dragData);
                  e.dataTransfer.effectAllowed = "move";
                  e.currentTarget.classList.add("opacity-50");
                  document.body.style.cursor = "move";
                } catch (error) {
                  console.error("Error in drag start:", error);
                }
              };

              const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
                if (e.currentTarget) {
                  e.currentTarget.classList.remove("opacity-50");
                }
                document.body.style.cursor = "default";
                setIsDragOver(false);
              };

              // Get connections to display
              const connectionsToShow = connectionsForEntry
                .slice(0, maxVisibleConnections)
                .map((connId) => post.connections[connId])
                .filter(Boolean);

              const totalConnections = connectionsForEntry.length;
              const timeToShow = scheduledFor ?? publishedAt;
              const timeLabel = timeToShow
                ? format(new Date(toMilliseconds(timeToShow)), "p")
                : "";
              const postLabel =
                post.description?.trim() ||
                post.title?.trim() ||
                `${post.mediaItems?.[0]?.type === "video" ? "Video" : "Image"} post`;
              const statusLabel = isBatchGenerating
                ? "Generating"
                : isBatchDraft
                  ? "Draft"
                  : getConnectionStatusLabel(
                      connectionsForEntry
                        .map((connId) => post.connections[connId]?.status)
                        .filter(
                          (
                            status,
                          ): status is NonNullable<
                            (typeof post.connections)[string]["status"]
                          > => Boolean(status),
                        ),
                    );

              return (
                <EntryQuickPeek
                  key={`${post.id}-${connectionId}`}
                  post={post}
                  connectionIds={connectionsForEntry}
                  connectionsById={connectionsById}
                  statusLabel={statusLabel}
                  mediaUrls={mediaUrls}
                  onOpenPost={() => onPostSelect(post)}
                  disabled={isBatchGenerating}
                >
                  <div
                    onClick={
                      isBatchGenerating ? undefined : () => onPostSelect(post)
                    }
                    draggable={isPending || isBatchDraft}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    role={isBatchGenerating ? undefined : "button"}
                    tabIndex={isBatchGenerating ? -1 : 0}
                    onKeyDown={
                      isBatchGenerating
                        ? undefined
                        : (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              // Synthesize a click so the quick-peek trigger's own click
                              // handler also runs and closes/suppresses the hover card
                              // before the PostViewer opens.
                              e.currentTarget.click();
                            }
                          }
                    }
                    className={cn(
                      "post-item",
                      "block w-full max-w-full min-w-0 group relative overflow-hidden",
                      "hover:shadow-md hover:bg-[#FF5C35]/5",
                      "transition-all duration-150 ease-in-out",
                      useMonthTwoRowLayout
                        ? "h-full"
                        : isMonthView
                          ? "mb-1.5 last:mb-0"
                          : "mb-2 last:mb-0",
                      isPending || isBatchDraft
                        ? "cursor-move"
                        : "cursor-pointer",
                      "focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:ring-opacity-50",
                      // Batch drafts/generating: dashed border in post color (ghost mode)
                      isBatchDraft || isBatchGenerating
                        ? "border border-dashed rounded-md opacity-80"
                        : // Settled batch/grouped posts: solid left border
                          (isGrouped || isBatchPost) &&
                            "border-l-4 rounded-l-sm",
                    )}
                    style={
                      isBatchDraft || isBatchGenerating
                        ? { borderColor: postColor }
                        : isGrouped || isBatchPost
                          ? { borderLeftColor: postColor }
                          : undefined
                    }
                  >
                    {isMonthView ? (
                      <div
                        className={cn(
                          "flex items-start px-2.5 py-1.5",
                          useMonthTwoRowLayout ? "h-full" : "h-[56px]",
                        )}
                      >
                        <div className="flex flex-col flex-grow min-w-0 gap-1">
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <p className="text-[11px] md:text-xs font-medium text-gray-900 truncate min-w-0 flex-1">
                              <span className="notranslate" translate="no">
                                {postLabel}
                              </span>
                            </p>
                            {timeLabel && (
                              <span className="text-[10px] md:text-xs text-gray-500 shrink-0">
                                {timeLabel}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                            <span
                              className={cn(
                                "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0",
                                getStatusBadgeClass(statusLabel),
                              )}
                            >
                              {statusLabel}
                            </span>

                            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                              {connectionsToShow.map((conn) => {
                                if (!conn) return null;
                                const connectionDetails =
                                  connectionsById[conn.connectionId];
                                if (!connectionDetails) return null;

                                return (
                                  <div
                                    key={conn.connectionId}
                                    className="shrink-0"
                                    title={`${connectionDetails.profileName}: ${
                                      conn.publishedAt
                                        ? `Published at ${formatDate(conn.publishedAt)}`
                                        : conn.status === "failed" ? "Failed" : conn.status === "pre-processing" ? "Preparing" : conn.status === "publishing" ? "Publishing" : !conn.scheduledFor && !post.scheduledFor ? "Queued" : `Scheduled for ${formatDate(conn.scheduledFor ?? post.scheduledFor)}`
                                    }`}
                                  >
                                    <PlatformConnectionIcon
                                      platform={connectionDetails.platform}
                                      profileImage={
                                        connectionDetails.profileImage
                                      }
                                      size="xs"
                                      disableTooltip={true}
                                    />
                                  </div>
                                );
                              })}
                            </div>

                            {totalConnections > maxVisibleConnections && (
                              <div
                                className={cn(
                                  "rounded-full flex items-center justify-center text-xs font-medium",
                                  "bg-gray-100 text-gray-600",
                                  "min-w-[20px] h-[20px] px-1 shrink-0",
                                )}
                              >
                                +{totalConnections - maxVisibleConnections}
                              </div>
                            )}
                            {isGrouped && (
                              <div
                                className="text-[9px] font-medium px-1 py-0.5 rounded shrink-0"
                                style={{
                                  backgroundColor: `${postColor}20`,
                                  color: postColor,
                                }}
                              >
                                {groupIndex}/{groupTotal}
                              </div>
                            )}
                          </div>
                        </div>
                        {isPending && !isBatchGenerating && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 ml-1 text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCancelSchedule(post.id, connectionsForEntry);
                            }}
                            title="Remove from post"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="p-2 space-y-2 min-w-0">
                        {post.mediaItems?.[0] && (
                          <div className="flex justify-center rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                            <SlidePreview
                              mediaItem={post.mediaItems[0]}
                              mediaUrls={mediaUrls}
                              className="h-36 max-w-full"
                            />
                          </div>
                        )}

                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate min-w-0 flex-1">
                            <span className="notranslate" translate="no">
                              {postLabel}
                            </span>
                          </p>
                          {timeLabel && (
                            <span className="text-xs text-gray-500 shrink-0">
                              {timeLabel}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                              getStatusBadgeClass(statusLabel),
                            )}
                          >
                            {statusLabel}
                          </span>
                          {isGrouped && (
                            <div
                              className="text-xs font-medium px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: `${postColor}20`,
                                color: postColor,
                              }}
                            >
                              {groupIndex}/{groupTotal}
                            </div>
                          )}
                          {isPending && !isBatchGenerating && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 ml-auto text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCancelSchedule(post.id, connectionsForEntry);
                              }}
                              title="Remove from post"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap">
                          {connectionsToShow.map((conn) => {
                            if (!conn) return null;
                            const connectionDetails =
                              connectionsById[conn.connectionId];
                            if (!connectionDetails) return null;

                            return (
                              <div
                                key={conn.connectionId}
                                className="shrink-0"
                                title={`${connectionDetails.profileName}: ${
                                  conn.publishedAt
                                    ? `Published at ${formatDate(conn.publishedAt)}`
                                    : conn.status === "failed" ? "Failed" : conn.status === "pre-processing" ? "Preparing" : conn.status === "publishing" ? "Publishing" : !conn.scheduledFor && !post.scheduledFor ? "Queued" : `Scheduled for ${formatDate(conn.scheduledFor ?? post.scheduledFor)}`
                                }`}
                              >
                                <PlatformConnectionIcon
                                  platform={connectionDetails.platform}
                                  profileImage={connectionDetails.profileImage}
                                  size="sm"
                                  disableTooltip={true}
                                />
                              </div>
                            );
                          })}
                          {totalConnections > maxVisibleConnections && (
                            <div className="rounded-full bg-gray-100 text-gray-600 min-w-[24px] h-[24px] px-1 text-xs font-medium flex items-center justify-center">
                              +{totalConnections - maxVisibleConnections}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </EntryQuickPeek>
              );
            })}
          </div>
          {isMonthView && hasMonthToggle && (
            <button
              type="button"
              className="h-5 mt-1 w-full rounded-md px-2 text-[11px] leading-5 font-medium text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                setIsMonthExpanded((prev) => !prev);
              }}
            >
              {isMonthExpanded
                ? "Show less"
                : `+${dayEntries.length - MAX_VISIBLE_MONTH_ENTRIES} more`}
            </button>
          )}
        </div>
      ) : (
        isDragOver ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500 py-4">
            <div className="bg-[#FF5C35]/5 px-4 py-2 rounded-lg">
              Drop to schedule
            </div>
          </div>
        ) : !isMonthView ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No posts
          </p>
        ) : null
      )}
    </div>
  );
});

// Memoize the SimpleTimePicker component
const SimpleTimePicker = memo(function SimpleTimePicker({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  initialDate,
  initialTime,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: string, time: string) => void;
  isLoading: boolean;
  initialDate: Date;
  initialTime: string;
}) {
  const [mounted, setMounted] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedTime, setSelectedTime] = useState("12:00");
  const [selectedDate, setSelectedDate] = useState(
    format(initialDate, "yyyy-MM-dd"),
  );

  // Update selected time when initialTime changes
  useEffect(() => {
    setSelectedTime(initialTime);
  }, [initialTime]);

  // Update selected date when initialDate changes
  useEffect(() => {
    setSelectedDate(format(initialDate, "yyyy-MM-dd"));
  }, [initialDate]);

  // Handle mounting
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle body overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Debounced time change handler
  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setIsTyping(true);
      setSelectedTime(newTime);

      // Clear typing state after a short delay
      setTimeout(() => {
        setIsTyping(false);
      }, 300);
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (!isTyping) {
      onConfirm(selectedDate, selectedTime);
    }
  }, [selectedDate, selectedTime, onConfirm, isTyping]);

  // Early return if not mounted or not open
  if (!isOpen || !mounted) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div
        className="bg-white p-6 rounded-xl shadow-xl w-[425px] relative border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Select Date & Time
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              className={cn(
                "w-full px-3 py-2 border rounded-lg text-gray-900 placeholder-gray-400",
                "focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:ring-opacity-50 focus:border-transparent",
                "transition duration-200",
                isLoading && "opacity-50 cursor-not-allowed",
              )}
              onChange={(e) => setSelectedDate(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Time
            </label>
            <input
              ref={timeInputRef}
              type="time"
              value={selectedTime}
              className={cn(
                "w-full px-3 py-2 border rounded-lg text-gray-900 placeholder-gray-400",
                "focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:ring-opacity-50 focus:border-transparent",
                "transition duration-200",
                isLoading && "opacity-50 cursor-not-allowed",
              )}
              onChange={handleTimeChange}
              disabled={isLoading}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                "border border-gray-200 text-gray-700",
                "hover:bg-gray-50 hover:text-gray-900",
                "focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:ring-opacity-50",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg transition-all",
                "bg-[#FF5C35] text-white",
                "hover:bg-[#FF5C35]/90",
                "focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:ring-opacity-50",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "relative",
              )}
              onClick={handleConfirm}
              disabled={isLoading || isTyping}
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">Reschedule</span>
                  <Loader2 className="h-4 w-4 animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </>
              ) : (
                "Reschedule"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const portalElement = document.getElementById("modal-root");
  if (!portalElement) {
    console.error("Modal root element not found");
    return null;
  }

  return createPortal(modalContent, portalElement);
});

const WeeklyView = memo(function WeeklyView({
  selectedDate,
  entriesByDate,
  connectionsById,
  onPostSelect,
  onDrop,
  onCancelSchedule,
  isMobile,
}: {
  selectedDate: Date;
  entriesByDate: Record<string, CalendarEntry[]>;
  connectionsById: Record<string, PlatformConnection>;
  onPostSelect: (post: Post) => void;
  onDrop: (postId: string, connectionIds: string[], date: Date) => void;
  onCancelSchedule: (postId: string, connectionIds: string[]) => void;
  isMobile: boolean;
}) {
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index),
  );

  if (isMobile) {
    return (
      <div className="space-y-3 p-3 w-full max-w-full overflow-x-hidden">
        {weekDays.map((date) => (
          <div
            key={date.toISOString()}
            className="rounded-lg border border-gray-200 bg-white p-2 w-full max-w-full min-w-0 overflow-hidden"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-800">
                {format(date, "EEE")}
              </p>
              <p className="text-xs text-gray-500">{format(date, "MMM d")}</p>
            </div>
            <TileContent
              date={date}
              view="week"
              entriesByDate={entriesByDate}
              connectionsById={connectionsById}
              onPostSelect={onPostSelect}
              onDrop={onDrop}
              onCancelSchedule={onCancelSchedule}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="grid grid-cols-7 gap-3 p-3 w-full">
        {weekDays.map((date) => (
          <div
            key={date.toISOString()}
            className="rounded-lg border border-gray-200 bg-white p-2 min-w-0 overflow-hidden"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-800">
                {format(date, "EEE")}
              </p>
              <p className="text-xs text-gray-500">{format(date, "MMM d")}</p>
            </div>
            <TileContent
              date={date}
              view="week"
              entriesByDate={entriesByDate}
              connectionsById={connectionsById}
              onPostSelect={onPostSelect}
              onDrop={onDrop}
              onCancelSchedule={onCancelSchedule}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export type ContentCalendarView = "month" | "week";

export interface ContentCalendarProps {
  batchId?: string;
  initialDate?: Date;
  defaultView?: ContentCalendarView;
  selectedDate?: Date;
  calendarView?: ContentCalendarView;
  onSelectedDateChange?: (date: Date) => void;
  onCalendarViewChange?: (view: ContentCalendarView) => void;
  onBatchComplete?: () => void;
  onBatchDismiss?: () => void;
  embedded?: boolean;
}

export default function ContentCalendar({
  batchId: propBatchId,
  initialDate: propInitialDate,
  defaultView: propDefaultView,
  selectedDate: propSelectedDate,
  calendarView: propCalendarView,
  onSelectedDateChange,
  onCalendarViewChange,
  onBatchComplete,
  onBatchDismiss,
  embedded,
}: ContentCalendarProps = {}) {
  const { currentUser } = useAuth();
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (propSelectedDate) return propSelectedDate;
    if (propInitialDate) return propInitialDate;
    const dateParam = new URLSearchParams(window.location.search).get("date");
    if (dateParam) {
      const parsed = new Date(dateParam);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [isMobile, setIsMobile] = useState(false);
  const [calendarView, setCalendarView] = useState<ContentCalendarView>(
    propCalendarView ?? propDefaultView ?? "month",
  );
  const [timePickerState, setTimePickerState] = useState<{
    isOpen: boolean;
    postId: string;
    connectionIds: string[]; // empty = all connections
    date: Date;
    initialTime: string;
  }>({
    isOpen: false,
    postId: "",
    connectionIds: [],
    date: new Date(),
    initialTime: "12:00",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [cancelConfirmState, setCancelConfirmState] = useState<{
    isOpen: boolean;
    postId: string;
    connectionIds: string[]; // empty = cancel all
    platformName: string;
  }>({
    isOpen: false,
    postId: "",
    connectionIds: [],
    platformName: "",
  });

  const changeSelectedDate = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      onSelectedDateChange?.(date);
    },
    [onSelectedDateChange],
  );

  const changeCalendarView = useCallback(
    (view: ContentCalendarView) => {
      setCalendarView(view);
      onCalendarViewChange?.(view);
    },
    [onCalendarViewChange],
  );

  useEffect(() => {
    if (propSelectedDate) {
      setSelectedDate(propSelectedDate);
    }
  }, [propSelectedDate]);

  useEffect(() => {
    if (propCalendarView) {
      setCalendarView(propCalendarView);
    }
  }, [propCalendarView]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleMediaQueryChange);
    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile && calendarView !== "week") {
      changeCalendarView("week");
    }
  }, [calendarView, changeCalendarView, isMobile]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBatchId = propBatchId ?? searchParams.get("batchId");

  const clearBatchSearchParam = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("batchId");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const {
    posts: allPosts,
    batchPosts,
    loading,
    error,
  } = useScheduledPosts({ batchId: activeBatchId });

  const batchDraftCount = batchPosts.filter((p) => p.status === "draft").length;
  const batchGeneratingCount = batchPosts.filter(
    (p) => p.status === "generating",
  ).length;
  const batchReadyCount = batchDraftCount;

  const handleDiscardBatch = useCallback(() => {
    if (onBatchDismiss) {
      onBatchDismiss();
    } else {
      clearBatchSearchParam();
    }
  }, [clearBatchSearchParam, onBatchDismiss]);

  // Auto-dismiss batch dock once all posts have been scheduled (no more drafts/generating)
  useEffect(() => {
    if (!activeBatchId || batchPosts.length === 0) return;
    const hasPending = batchPosts.some(
      (p) => p.status === "draft" || p.status === "generating",
    );
    if (!hasPending) {
      if (onBatchComplete) {
        onBatchComplete();
      } else {
        clearBatchSearchParam();
      }
    }
  }, [activeBatchId, batchPosts, clearBatchSearchParam, onBatchComplete]);

  // Create calendar entries - one per connection when times differ
  const entriesByDate = useMemo(() => {
    const grouped: Record<string, CalendarEntry[]> = {};

    allPosts?.forEach((post) => {
      const connections = Object.entries(post.connections);
      // For batch posts, use sequential batchIndex for guaranteed color spread
      const postColor =
        post.batchId && post.batchIndex != null
          ? POST_COLORS[post.batchIndex % POST_COLORS.length]
          : getPostColor(post.id);
      const times = calendarConnectionTimes(post);
      const connectionTimes = connections.map(([connectionId, conn]) => ({
        connectionId,
        time: times[connectionId],
        status: conn.status,
        publishedAt: conn.publishedAt,
      }));

      // Group connections by their exact time (regardless of status)
      const timeToConnections = new Map<number, string[]>();
      connectionTimes.forEach(({ connectionId, time }) => {
        if (!time) return;
        if (!timeToConnections.has(time)) {
          timeToConnections.set(time, []);
        }
        timeToConnections.get(time)!.push(connectionId);
      });

      const uniqueTimeSlots = Array.from(timeToConnections.keys()).sort(
        (a, b) => a - b,
      );
      const totalTimeSlots = uniqueTimeSlots.length;

      if (totalTimeSlots === 1) {
        // All connections at same time - single entry showing all
        const time = uniqueTimeSlots[0];
        const dateKey = getDateKey(time);
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        const firstConnId = timeToConnections.get(time)![0];
        const conn = post.connections[firstConnId];

        grouped[dateKey].push({
          post,
          connectionId: "__all__", // Special marker for "show all connections"
          scheduledFor: conn.status !== "published" ? time : undefined,
          publishedAt:
            conn.status === "published" ? time : undefined,
          status: post.status,
          groupIndex: 1,
          groupTotal: 1,
          postColor,
        });
      } else {
        // Multiple time slots - create one entry per time slot
        uniqueTimeSlots.forEach((time, idx) => {
          const connectionIds = timeToConnections.get(time)!;
          const dateKey = getDateKey(time);
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }

          // Use a special marker that includes the time to identify this group
          // Format: "__time_<timestamp>" for grouped connections at same time
          const connectionMarker =
            connectionIds.length > 1 ? `__time_${time}` : connectionIds[0];

          const firstConnId = connectionIds[0];
          const conn = post.connections[firstConnId];

          grouped[dateKey].push({
            post,
            connectionId: connectionMarker,
            scheduledFor: conn.status !== "published" ? time : undefined,
            publishedAt:
              conn.status === "published" ? time : undefined,
            status: conn.status,
            groupIndex: idx + 1,
            groupTotal: totalTimeSlots,
            postColor,
          });
        });
      }
    });

    return grouped;
  }, [allPosts]);

  const { connections: workspaceConnections } = useWorkspaceConnections();

  const connectionsById = useMemo(() => {
    const connections: Record<string, PlatformConnection> = {};
    workspaceConnections.forEach((connection) => {
      connections[connection.id] = connection;
    });
    return connections;
  }, [workspaceConnections]);

  // Add global drag end handler
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      document.body.style.cursor = "default";
    };

    document.addEventListener("dragend", handleGlobalDragEnd);
    return () => {
      document.removeEventListener("dragend", handleGlobalDragEnd);
    };
  }, []);

  const handleDrop = useCallback(
    (postId: string, connectionIds: string[], newDate: Date) => {
      console.log("handleDrop called:", { postId, connectionIds, newDate });
      // Reset any lingering drag states
      document.body.style.cursor = "default";

      // Find the post to get its current scheduled time
      const post = allPosts?.find((p) => p.id === postId);
      let initialTime = "12:00";

      // Get the initial time from the first connection or post
      if (
        connectionIds.length > 0 &&
        post?.connections[connectionIds[0]]?.scheduledFor
      ) {
        const connTime = post.connections[connectionIds[0]].scheduledFor;
        const fullDateTime = formatDate(connTime, "datetime-local");
        initialTime = fullDateTime.split("T")[1];
      } else if (post?.scheduledFor) {
        const fullDateTime = formatDate(post.scheduledFor, "datetime-local");
        initialTime = fullDateTime.split("T")[1];
      }

      setTimePickerState({
        isOpen: true,
        postId,
        connectionIds,
        date: newDate,
        initialTime,
      });
    },
    [allPosts],
  );

  // Handle reschedule action from menu

  // Show cancel confirmation dialog
  // connectionIds: specific connections to cancel, or empty array for all
  const handleCancelSchedule = useCallback(
    (postId: string, connectionIds: string[]) => {
      const post = allPosts?.find((p) => p.id === postId);
      let platformName = "all platforms";
      if (connectionIds.length === 1 && post) {
        const connection = post.connections[connectionIds[0]];
        platformName = connection?.platform ?? "this platform";
      } else if (connectionIds.length > 1 && post) {
        const names = connectionIds
          .map((id) => post.connections[id]?.platform)
          .filter(Boolean);
        platformName = names.join(" & ") || "selected platforms";
      }

      setCancelConfirmState({
        isOpen: true,
        postId,
        connectionIds,
        platformName,
      });
    },
    [allPosts],
  );

  // Actually perform the cancel after confirmation
  const performCancelSchedule = useCallback(async () => {
    const { postId, connectionIds } = cancelConfirmState;
    if (!currentUser || !postId) return;

    try {
      setIsUpdating(true);
      const idToken = await currentUser.getIdToken();

      if (connectionIds.length === 0) {
        // Cancel all connections
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/schedule/cancel`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ postId, connectionId: null }),
          },
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        // Cancel specific connections one by one
        for (const connId of connectionIds) {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE}/schedule/cancel`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ postId, connectionId: connId }),
            },
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
      }
    } catch (error) {
      console.error("Error cancelling schedule:", error);
      toast({
        title: "Error",
        description: "Failed to cancel schedule. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
      setCancelConfirmState((prev) => ({ ...prev, isOpen: false }));
    }
  }, [currentUser, cancelConfirmState]);

  const handleTimePickerClose = useCallback(() => {
    setTimePickerState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const updatePostSchedule = useCallback(
    async (
      postId: string,
      connectionIds: string[],
      newScheduledTime: number,
      postStatus: string,
    ) => {
      console.log("updatePostSchedule called:", {
        postId,
        connectionIds,
        newScheduledTime,
        postStatus,
      });
      try {
        // Validate schedule time is in the future
        if (newScheduledTime <= Date.now()) {
          toast({
            title: "Invalid time",
            description: "Schedule time must be in the future.",
            variant: "destructive",
          });
          setIsUpdating(false);
          handleTimePickerClose();
          return;
        }

        if (postStatus === "draft") {
          // Draft: just update times directly in Firestore (no Cloud Tasks)
          const postRef = doc(db, "posts", postId);
          const updates: Record<string, any> = { updatedAt: Date.now() };

          if (connectionIds.length > 0) {
            for (const connId of connectionIds) {
              updates[`connections.${connId}.scheduledFor`] = newScheduledTime;
            }
          } else {
            updates.scheduledFor = newScheduledTime;
            // No connection-level update needed — caller didn't specify connections
          }

          await updateDoc(postRef, updates);
        } else {
          // Scheduled/published: call /schedule to reschedule Cloud Tasks
          const idToken = await currentUser?.getIdToken();

          const body: {
            postId: string;
            scheduledFor: number;
            connectionSchedules?: Record<string, number>;
            connectionId?: string;
          } = {
            postId,
            scheduledFor: newScheduledTime,
          };

          if (connectionIds.length > 0) {
            const schedules: Record<string, number> = {};
            for (const connId of connectionIds) {
              schedules[connId] = newScheduledTime;
            }
            body.connectionSchedules = schedules;
            if (connectionIds.length === 1) {
              body.connectionId = connectionIds[0];
            }
          }

          const response = await fetch(
            `${process.env.REACT_APP_API_BASE}/schedule`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(body),
            },
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const previous = allPosts.find((p) => p.id === postId);
        const previousRaw =
          (connectionIds.length > 0
            ? previous?.connections[connectionIds[0]]?.scheduledFor
            : undefined) ?? previous?.scheduledFor;
        // Legacy docs store epoch seconds; unnormalized, deltaHours comes out in the millions.
        const previousScheduledFor = previousRaw
          ? toMilliseconds(previousRaw)
          : undefined;

        posthog.capture("post_rescheduled", {
          postId,
          source: "calendar",
          postStatus,
          connectionCount: connectionIds.length,
          oldScheduledFor: previousScheduledFor,
          newScheduledFor: newScheduledTime,
          deltaHours: previousScheduledFor
            ? Math.round((newScheduledTime - previousScheduledFor) / 3_600_000)
            : undefined,
        });

        toast({
          title: postStatus === "draft" ? "Time updated" : "Rescheduled",
          description:
            connectionIds.length > 0
              ? `${connectionIds.length} platform${connectionIds.length > 1 ? "s" : ""} ${postStatus === "draft" ? "updated" : "rescheduled"} to ${format(newScheduledTime, "PPp")}`
              : `Post ${postStatus === "draft" ? "updated" : "rescheduled"} to ${format(newScheduledTime, "PPp")}`,
        });
      } catch (error) {
        console.error("Error updating schedule:", error);
        toast({
          title: "Error",
          description: "Failed to update schedule. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsUpdating(false);
        handleTimePickerClose();
      }
    },
    [currentUser, handleTimePickerClose, allPosts],
  );

  const handleTimeConfirm = useCallback(
    async (selectedDate: string, time: string) => {
      const { postId, connectionIds } = timePickerState;
      console.log("handleTimeConfirm called:", {
        postId,
        connectionIds,
        selectedDate,
        time,
      });
      if (!currentUser || !allPosts || isUpdating) return;

      const post = allPosts.find((p) => p.id === postId);
      if (!post) {
        console.error("Post not found:", postId);
        toast({
          title: "Error",
          description: "Could not find the post to reschedule.",
          variant: "destructive",
        });
        return;
      }

      setIsUpdating(true);

      const newScheduledTime = new Date(`${selectedDate}T${time}:00`).getTime();

      updatePostSchedule(postId, connectionIds, newScheduledTime, post.status);
    },
    [timePickerState, currentUser, allPosts, isUpdating, updatePostSchedule],
  );

  // The embedded Posts calendar owns its compact period navigation.
  const moveVisiblePeriod = (amount: number) => {
    changeSelectedDate(
      calendarView === "month"
        ? addMonths(selectedDate, amount)
        : addDays(selectedDate, amount * 7),
    );
  };

  const visiblePeriodLabel =
    calendarView === "month"
      ? format(selectedDate, "MMMM yyyy")
      : `${format(startOfWeek(selectedDate, { weekStartsOn: 0 }), "MMM d")} – ${format(
          addDays(startOfWeek(selectedDate, { weekStartsOn: 0 }), 6),
          "MMM d",
        )}`;

  if (loading) {
    return (
      <Card className={cn(embedded && "border-0 shadow-none")}>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn(embedded && "border-0 shadow-none")}>
        <CardContent>
          <div className="text-center text-red-600 p-4">
            <h3 className="font-semibold mb-2">Error loading posts</h3>
            <p className="text-sm">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        className={cn(
          "bg-white w-full max-w-full overflow-x-hidden",
          embedded ? "shadow-none border-0" : "shadow-sm border-0",
        )}
      >
        <CardHeader
          className={cn(
            "border-b border-gray-100",
            embedded ? "space-y-0 px-3 py-2 sm:px-4" : "pb-4",
          )}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between min-w-0">
            {embedded ? (
              <div className="flex min-w-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveVisiblePeriod(-1)}
                  aria-label={`Previous ${calendarView}`}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <p className="min-w-0 flex-1 truncate px-1 text-center text-sm font-semibold text-gray-800 sm:min-w-36">
                  {visiblePeriodLabel}
                </p>
                <button
                  type="button"
                  onClick={() => moveVisiblePeriod(1)}
                  aria-label={`Next ${calendarView}`}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <CardTitle className="text-xl font-semibold text-gray-900">
                Content Calendar
              </CardTitle>
            )}
            <div
              className={cn(
                "flex items-center gap-1 flex-wrap sm:justify-end min-w-0",
                embedded
                  ? "justify-between sm:w-auto"
                  : "justify-between w-full sm:w-auto",
              )}
            >
              {!embedded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/ideas")}
                  className="text-primary border-primary/20 hover:bg-primary/5"
                >
                  <CalendarDays className="w-4 h-4 mr-1" />
                  Suggested Posts
                </Button>
              )}
              <div className="inline-flex rounded-md border border-gray-200 p-0.5">
                <button
                  type="button"
                  onClick={() => changeCalendarView("month")}
                  aria-pressed={calendarView === "month"}
                  className={cn(
                    "min-h-10 px-2.5 py-1 text-xs font-medium rounded",
                    calendarView === "month"
                      ? "bg-[#FF5C35] text-white"
                      : "text-gray-600 hover:bg-gray-50",
                  )}
                >
                  Month
                </button>
                <button
                  type="button"
                  onClick={() => changeCalendarView("week")}
                  aria-pressed={calendarView === "week"}
                  className={cn(
                    "min-h-10 px-2.5 py-1 text-xs font-medium rounded",
                    calendarView === "week"
                      ? "bg-[#FF5C35] text-white"
                      : "text-gray-600 hover:bg-gray-50",
                  )}
                >
                  Week
                </button>
              </div>
              <button
                type="button"
                onClick={() => changeSelectedDate(new Date())}
                className="min-h-11 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
              >
                Today
              </button>
            </div>
          </div>
          {!embedded && calendarView === "week" && (
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap min-w-0">
              <button
                onClick={() => changeSelectedDate(addDays(selectedDate, -7))}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 shrink-0"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Previous Week</span>
              </button>
              <p className="text-sm font-medium text-gray-700 truncate min-w-0 text-center flex-1">
                {format(
                  startOfWeek(selectedDate, { weekStartsOn: 0 }),
                  "MMM d",
                )}{" "}
                -{" "}
                {format(
                  addDays(startOfWeek(selectedDate, { weekStartsOn: 0 }), 6),
                  "MMM d",
                )}
              </p>
              <button
                onClick={() => changeSelectedDate(addDays(selectedDate, 7))}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 shrink-0"
              >
                <span className="hidden sm:inline">Next Week</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0 min-w-0 max-w-full overflow-x-hidden">
          {calendarView === "month" ? (
            <Calendar
              calendarType="gregory"
              tileContent={({ date, view }) => (
                <TileContent
                  date={date}
                  view={view}
                  entriesByDate={entriesByDate}
                  connectionsById={connectionsById}
                  onPostSelect={setSelectedPost}
                  onDrop={handleDrop}
                  onCancelSchedule={handleCancelSchedule}
                />
              )}
              className={cn(
                "content-calendar",
                embedded && "content-calendar--embedded",
              )}
              showNavigation={!embedded}
              tileClassName={({ date }) => {
                const dateStr = getDateKey(date.getTime());
                return cn(
                  entriesByDate[dateStr]?.length > 0 && "has-posts",
                  "hover:bg-gray-50/80 transition-colors",
                );
              }}
              nextLabel={<ChevronRight className="h-4 w-4" />}
              prevLabel={<ChevronLeft className="h-4 w-4" />}
              next2Label={null}
              prev2Label={null}
              minDetail="month"
              maxDetail="month"
              activeStartDate={startOfMonth(selectedDate)}
              onActiveStartDateChange={({ activeStartDate }) => {
                if (
                  activeStartDate &&
                  !isSameMonth(activeStartDate, selectedDate)
                ) {
                  changeSelectedDate(activeStartDate);
                }
              }}
              onClickMonth={() => {}}
              onClickYear={() => {}}
              onChange={(value) => {
                if (value instanceof Date) {
                  changeSelectedDate(value);
                }
              }}
              value={selectedDate}
            />
          ) : (
            <WeeklyView
              selectedDate={selectedDate}
              entriesByDate={entriesByDate}
              connectionsById={connectionsById}
              onPostSelect={setSelectedPost}
              onDrop={handleDrop}
              onCancelSchedule={handleCancelSchedule}
              isMobile={isMobile}
            />
          )}
        </CardContent>
      </Card>

      {/* Spacer for fixed batch dock */}
      {activeBatchId && batchPosts.length > 0 && <div className="h-16" />}

      {/* Batch approval dock */}
      {activeBatchId && batchPosts.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-50 animate-in slide-in-from-bottom duration-300">
          {/* Gradient top edge */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          {/* Progress bar */}
          {batchGeneratingCount > 0 && (
            <div className="h-0.5 bg-gray-200/50">
              <div
                className="h-full bg-primary transition-all duration-700 ease-out"
                style={{
                  width: `${batchPosts.length > 0 ? (batchReadyCount / batchPosts.length) * 100 : 0}%`,
                }}
              />
            </div>
          )}
          <div className="backdrop-blur-xl bg-white/80 border-t border-gray-200/50 px-6 py-3">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                {batchGeneratingCount > 0 ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                ) : (
                  <Sparkles className="w-4 h-4 text-primary" />
                )}
                <span className="text-sm font-medium text-gray-800">
                  {batchGeneratingCount > 0
                    ? `${batchReadyCount} of ${batchPosts.length} posts ready`
                    : `${batchReadyCount} post${batchReadyCount !== 1 ? "s" : ""} ready for review`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDiscardBatch}
                        disabled={batchGeneratingCount > 0}
                        className={
                          batchGeneratingCount > 0
                            ? "text-gray-300 opacity-50 cursor-not-allowed"
                            : "text-gray-500 hover:text-gray-800"
                        }
                      >
                        <X className="w-4 h-4 mr-1" />
                        Done
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>
                        Posts are saved as drafts. Schedule them anytime from
                        Drafts.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPost && (
        <PostViewer
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          expandedPost={selectedPost}
          initialIndex={0}
          connectionsById={connectionsById}
        />
      )}

      <SimpleTimePicker
        isOpen={timePickerState.isOpen}
        onClose={handleTimePickerClose}
        onConfirm={handleTimeConfirm}
        isLoading={isUpdating}
        initialDate={timePickerState.date}
        initialTime={timePickerState.initialTime}
      />

      <AlertDialog
        open={cancelConfirmState.isOpen}
        onOpenChange={(open) =>
          setCancelConfirmState((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelConfirmState.connectionIds.length > 0 ? (
                <>
                  This will remove{" "}
                  <strong>{cancelConfirmState.platformName}</strong> from this
                  post. You'll need to re-add{" "}
                  {cancelConfirmState.connectionIds.length === 1
                    ? "it"
                    : "them"}{" "}
                  if you want to post to{" "}
                  {cancelConfirmState.connectionIds.length === 1
                    ? "this platform"
                    : "these platforms"}{" "}
                  later.
                </>
              ) : (
                <>
                  This will cancel the schedule for all platforms and reset the
                  post to draft status.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>
              Keep Schedule
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={performCancelSchedule}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel Schedule"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
